import React, { useState, useEffect } from 'react';
import { X, Lightbulb, Code, BookOpen, Zap, CheckCircle, FileText } from 'lucide-react';

const StructuredDataAIHelpDialog = ({ isOpen, onClose, context, contextData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);

  // Reset AI response when context changes
  useEffect(() => {
    setAiResponse(null);
  }, [context]);

  if (!isOpen) return null;

  const generateAIHelp = async () => {
    setIsLoading(true);
    try {
      // This would typically call your backend API with the AI key
      // For now, I'll simulate the AI response with context-specific guidance
      setTimeout(() => {
        if (context === 'sitemap') {
          setAiResponse({
            overview: "A sitemap is an XML file that lists all the important pages on your website, helping search engines discover and index your content more efficiently.",
            currentStatus: contextData?.present ? "Great! You have a sitemap implemented." : "You need to create and implement a sitemap for your website.",
            recommendations: [
              "Create a sitemap.xml file in your website's root directory",
              "Include all important pages with their URLs and last modified dates",
              "Submit your sitemap to Google Search Console and Bing Webmaster Tools",
              "Keep your sitemap updated when you add or remove pages",
              "Consider creating separate sitemaps for different content types"
            ],
            examples: [
              {
                type: "Basic Sitemap",
                code: `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>https://example.com/</loc>
    <lastmod>2024-01-01</lastmod>
    <changefreq>weekly</changefreq>
    <priority>1.0</priority>
  </url>
  <url>
    <loc>https://example.com/about</loc>
    <lastmod>2024-01-01</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>
</urlset>`
              },
              {
                type: "Sitemap Index",
                code: `<?xml version="1.0" encoding="UTF-8"?>
<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <sitemap>
    <loc>https://example.com/sitemap-pages.xml</loc>
    <lastmod>2024-01-01</lastmod>
  </sitemap>
  <sitemap>
    <loc>https://example.com/sitemap-products.xml</loc>
    <lastmod>2024-01-01</lastmod>
  </sitemap>
</sitemapindex>`
              }
            ],
            tools: [
              "Google Search Console",
              "Bing Webmaster Tools",
              "XML Sitemap Generator",
              "Screaming Frog SEO Spider",
              "Online XML Validator"
            ]
          });
        } else if (context === 'accessibility') {
          setAiResponse({
            overview: "Web accessibility ensures your website can be used by people with disabilities, including those using screen readers, keyboard navigation, and other assistive technologies.",
            currentStatus: contextData?.score >= 10 ? "Good accessibility implementation!" : "Your website needs accessibility improvements to serve all users effectively.",
            recommendations: [
              "Add ARIA labels to interactive elements for screen readers",
              "Implement skip links for keyboard navigation",
              "Ensure all form elements have proper labels",
              "Add descriptive alt text to all images",
              "Test your site with keyboard-only navigation",
              "Use sufficient color contrast for text readability",
              "Implement proper heading hierarchy (H1, H2, H3)"
            ],
            examples: [
              {
                type: "ARIA Labels",
                code: `<button aria-label="Close dialog" aria-describedby="dialog-desc">
  <span aria-hidden="true">×</span>
</button>
<div id="dialog-desc" class="sr-only">This will close the current dialog window</div>`
              },
              {
                type: "Skip Links",
                code: `<a href="#main-content" class="skip-link">
  Skip to main content
</a>

<main id="main-content" tabindex="-1">
  <!-- Main content here -->
</main>

<style>
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #000;
  color: white;
  padding: 8px;
  text-decoration: none;
  z-index: 100;
}
.skip-link:focus {
  top: 6px;
}
</style>`
              },
              {
                type: "Form Labels",
                code: `<label for="username">Username:</label>
<input type="text" id="username" name="username" aria-required="true">

<!-- Or use aria-label for complex forms -->
<input type="text" aria-label="Enter your email address" aria-required="true">`
              }
            ],
            tools: [
              "WAVE Web Accessibility Evaluator",
              "axe DevTools",
              "Lighthouse Accessibility Audit",
              "Color Contrast Analyzer",
              "Screen Reader Testing (NVDA, JAWS)",
              "Keyboard Navigation Testing"
            ]
          });
        } else if (context === 'performance') {
          setAiResponse({
            overview: "Website performance directly impacts user experience, search engine rankings, and conversion rates. Fast-loading pages improve user satisfaction and SEO performance.",
            currentStatus: contextData?.score >= 10 ? "Good performance optimization!" : "Your website needs performance improvements for better user experience and SEO.",
            recommendations: [
              "Move CSS files to the head section for faster rendering",
              "Load JavaScript files at the end of body for better page load",
              "Use minified CSS and JavaScript files to reduce file sizes",
              "Optimize and compress images for faster loading",
              "Enable GZIP compression on your server",
              "Implement browser caching for static resources",
              "Use a CDN for faster global content delivery",
              "Optimize critical rendering path"
            ],
            examples: [
              {
                type: "CSS in Head",
                code: `<head>
  <link rel="stylesheet" href="styles.css">
  <link rel="stylesheet" href="components.css">
</head>`
              },
              {
                type: "Scripts at End of Body",
                code: `<body>
  <!-- Page content here -->
  
  <!-- Load non-critical scripts at the end -->
  <script src="analytics.js"></script>
  <script src="app.js"></script>
</body>`
              },
              {
                type: "Minified Resources",
                code: `<!-- Use .min.css and .min.js files -->
<link rel="stylesheet" href="styles.min.css">
<script src="app.min.js"></script>

<!-- Or use build tools to automatically minify -->
<!-- webpack, gulp, or similar build processes -->`
              }
            ],
            tools: [
              "Google PageSpeed Insights",
              "GTmetrix",
              "WebPageTest",
              "Lighthouse Performance Audit",
              "Chrome DevTools Performance Tab",
              "WebPageTest.org",
              "Pingdom Tools"
            ]
          });
        } else if (context === 'contentQuality') {
          setAiResponse({
            overview: "High-quality content is the foundation of successful SEO. Well-written, comprehensive content that provides value to users will naturally rank better in search engines.",
            currentStatus: contextData?.score >= 10 ? "Excellent content quality!" : "Your content needs improvement for better SEO performance and user engagement.",
            recommendations: [
              "Create comprehensive, in-depth content (aim for 300+ words)",
              "Implement a clear internal linking strategy between related pages",
              "Use proper heading hierarchy (H1, H2, H3) for content structure",
              "Write unique, valuable content that answers user questions",
              "Include relevant keywords naturally throughout your content",
              "Add external links to authoritative sources when relevant",
              "Create content clusters around main topics",
              "Regularly update and expand existing content"
            ],
            examples: [
              {
                type: "Content Structure",
                code: `<h1>Main Page Title</h1>
<p>Introduction paragraph with key information...</p>

<h2>First Major Section</h2>
<p>Detailed content about this section...</p>

<h3>Subsection</h3>
<p>More specific information...</p>

<h2>Second Major Section</h2>
<p>Content about the second topic...</p>`
              },
              {
                type: "Internal Linking",
                code: `<p>Learn more about <a href="/seo-basics">SEO fundamentals</a> or explore our <a href="/advanced-techniques">advanced techniques guide</a>.</p>

<p>For specific strategies, see our <a href="/keyword-research">keyword research guide</a> and <a href="/content-optimization">content optimization tips</a>.</p>`
              },
              {
                type: "Content Clusters",
                code: `<!-- Pillar page: /seo-guide -->
<h1>Complete SEO Guide</h1>

<!-- Cluster pages linking to pillar -->
<a href="/seo-guide">← Back to main guide</a>
<h1>Keyword Research for SEO</h1>
<p>This is part of our comprehensive SEO guide...</p>`
              }
            ],
            tools: [
              "Google Keyword Planner",
              "Ahrefs Content Explorer",
              "SEMrush Topic Research",
              "Answer the Public",
              "Ubersuggest",
              "Google Trends",
              "Content Gap Analysis Tools"
            ]
          });
        } else if (context === 'metaTags') {
          setAiResponse({
            overview: "Meta tags are HTML elements that provide metadata about your webpage to search engines and browsers. They're crucial for SEO as they help search engines understand your content and display it properly in search results.",
            currentStatus: contextData?.score > 0 ? "Good! You have some meta tags implemented." : "Your website needs proper meta tags for better SEO performance.",
            recommendations: [
              "Add a unique, descriptive title tag (50-60 characters) for each page",
              "Include a compelling meta description (150-160 characters) that encourages clicks",
              "Add viewport meta tag for mobile optimization",
              "Consider adding Open Graph tags for social media sharing",
              "Use relevant keywords naturally in your title and description",
              "Ensure each page has unique meta tags (no duplicates)",
              "Keep meta descriptions engaging and action-oriented",
              "Test your meta tags with Google's Rich Results Test"
            ],
            examples: [
              {
                type: "Basic Meta Tags",
                code: `<!DOCTYPE html>
<html>
<head>
  <title>Your Page Title - Brand Name</title>
  <meta name="description" content="Your compelling description that explains what this page is about and encourages users to click.">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="keywords" content="relevant, keywords, for, your, page">
</head>
<body>
  <!-- Your content here -->
</body>
</html>`
              },
              {
                type: "Open Graph Tags",
                code: `<!-- Open Graph Meta Tags for Social Media -->
<meta property="og:title" content="Your Page Title">
<meta property="og:description" content="Your page description for social sharing">
<meta property="og:image" content="https://yoursite.com/image.jpg">
<meta property="og:url" content="https://yoursite.com/page-url">
<meta property="og:type" content="website">
<meta property="og:site_name" content="Your Site Name">

<!-- Twitter Card Meta Tags -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="Your Page Title">
<meta name="twitter:description" content="Your page description">
<meta name="twitter:image" content="https://yoursite.com/image.jpg">`
              },
              {
                type: "Advanced Meta Tags",
                code: `<!-- Canonical URL to prevent duplicate content -->
<link rel="canonical" href="https://yoursite.com/page-url">

<!-- Language and character encoding -->
<meta charset="UTF-8">
<meta http-equiv="Content-Language" content="en">

<!-- Robots meta tag for crawl control -->
<meta name="robots" content="index, follow">

<!-- Author and copyright information -->
<meta name="author" content="Your Name">
<meta name="copyright" content="Copyright 2024 Your Company">`
              }
            ],
            tools: [
              "Google Search Console",
              "Google Rich Results Test",
              "Facebook Sharing Debugger",
              "Twitter Card Validator",
              "Meta Tags Checker",
              "SEO Meta Tags Generator",
              "WordPress SEO Plugins (Yoast, RankMath)"
            ]
          });
        } else {
          // Default structured data response
          setAiResponse({
            overview: "Structured data helps search engines understand your content better, leading to rich snippets and improved visibility in search results.",
            currentStatus: contextData?.present ? "Good! You have structured data implemented." : "You need to add structured data to your website.",
            recommendations: [
              "Implement JSON-LD markup for your main content types",
              "Add schema markup for products, articles, or business information",
              "Use Google's Structured Data Testing Tool to validate your markup",
              "Ensure your structured data matches your actual content",
              "Consider implementing breadcrumb navigation markup"
            ],
            examples: [
              {
                type: "Article",
                code: `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Your Article Title",
  "author": {
    "@type": "Person",
    "name": "Author Name"
  },
  "datePublished": "2024-01-01"
}
</script>`
              },
              {
                type: "Product",
                code: `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Product Name",
  "description": "Product description",
  "offers": {
    "@type": "Offer",
    "price": "29.99",
    "priceCurrency": "USD"
  }
}
</script>`
              }
            ],
            tools: [
              "Google's Structured Data Testing Tool",
              "Schema.org markup validator",
              "Rich Results Test",
              "Structured Data Helper"
            ]
          });
        }
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error generating AI help:', error);
      setIsLoading(false);
    }
  };

  const getContextTitle = () => {
    if (context === 'sitemap') return 'AI Help: Sitemap';
    if (context === 'accessibility') return 'AI Help: Accessibility';
    if (context === 'performance') return 'AI Help: Performance';
    if (context === 'contentQuality') return 'AI Help: Content Quality';
    if (context === 'metaTags') return 'AI Help: Meta Tags';
    return 'AI Help: Structured Data';
  };

  const getContextIcon = () => {
    if (context === 'sitemap') return <FileText className="w-6 h-6 text-blue-600" />;
    if (context === 'accessibility') return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (context === 'performance') return <Zap className="w-6 h-6 text-yellow-600" />;
    if (context === 'contentQuality') return <BookOpen className="w-6 h-6 text-purple-600" />;
    if (context === 'metaTags') return <Code className="w-6 h-6 text-indigo-600" />;
    return <Lightbulb className="w-6 h-6 text-blue-600" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getContextIcon()}
            <h2 className="text-xl font-semibold text-gray-900">{getContextTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!aiResponse ? (
            <div className="text-center py-8">
              <Zap className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Get AI-Powered Guidance
              </h3>
              <p className="text-gray-600 mb-6">
                Let AI analyze your {context === 'sitemap' ? 'sitemap' : 
                                   context === 'accessibility' ? 'accessibility' : 
                                   context === 'performance' ? 'performance' : 
                                   context === 'contentQuality' ? 'content quality' : 
                                   'structured data'} and provide personalized recommendations for improvement.
              </p>
              <button
                onClick={generateAIHelp}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-4 h-4" />
                    <span>Generate AI Help</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {context === 'sitemap' ? 'What is a Sitemap?' : 
                   context === 'accessibility' ? 'What is Web Accessibility?' : 
                   context === 'performance' ? 'What is Website Performance?' : 
                   context === 'contentQuality' ? 'What is Content Quality?' : 
                   'What is Structured Data?'}
                </h3>
                <p className="text-blue-800 text-sm">{aiResponse.overview}</p>
              </div>

              {/* Current Status */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Your Current Status</h3>
                <p className="text-gray-700 text-sm">{aiResponse.currentStatus}</p>
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  Key Recommendations
                </h3>
                <div className="space-y-2">
                  {aiResponse.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 text-sm">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Code Examples */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Code className="w-5 h-5 text-purple-600 mr-2" />
                  {context === 'sitemap' ? 'Implementation Examples' : 'Implementation Examples'}
                </h3>
                <div className="space-y-4">
                  {aiResponse.examples.map((example, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-700">{example.type}</span>
                      </div>
                      <pre className="p-3 bg-gray-900 text-green-400 text-xs overflow-x-auto text-left">
                        <code>{example.code}</code>
                      </pre>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tools */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <BookOpen className="w-5 h-5 text-orange-600 mr-2" />
                  Useful Tools
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {aiResponse.tools.map((tool, index) => (
                    <div key={index} className="bg-gray-50 p-2 rounded text-sm text-gray-700">
                      {tool}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <div className="text-center pt-4">
                <button
                  onClick={onClose}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StructuredDataAIHelpDialog;
